import { createClient } from '@supabase/supabase-js';
import { TABELAS } from './protocolo.js';
export class ErroDeConta extends Error {
    name = 'ErroDeConta';
}
// A sessão vive no IndexedDB, não no localStorage: sobrevive melhor à
// evicção e funciona onde localStorage não existe.
export function armazenamentoEmDexie(banco) {
    const chave = (k) => `auth:${k}`;
    return {
        async getItem(k) {
            return (await banco.estado.get(chave(k)))?.valor ?? null;
        },
        async setItem(k, v) {
            await banco.estado.put({ chave: chave(k), valor: v });
        },
        async removeItem(k) {
            await banco.estado.delete(chave(k));
        },
    };
}
/**
 * `fetchImpl` existe por um motivo só: o supabase-js não entrega cabeçalho
 * de resposta nenhum, e o motor precisa do `Retry-After` de um 429 para
 * esperar o que o servidor pediu em vez de adivinhar. Quem o passa é o
 * `escutarRetryAfter` de `motor.ts`, e o mesmo objeto vai depois para o
 * `transporteSupabase`. Ausente, o cliente usa o `fetch` do aparelho.
 */
export function criarCliente(config, banco, fetchImpl) {
    return createClient(config.url, config.chave, {
        ...(fetchImpl === undefined ? {} : { global: { fetch: fetchImpl } }),
        auth: {
            storage: armazenamentoEmDexie(banco),
            persistSession: true,
            autoRefreshToken: true,
            // Nunca lemos tokens da URL: o login é por código, dentro do app.
            detectSessionInUrl: false,
            // A versão instalada do supabase-js (2.115) já tipa esta flag e os
            // métodos de passkey em GoTrueClient — sem necessidade de `as never`.
            experimental: { passkey: true },
        },
    });
}
export function traduzirErro(mensagem) {
    const m = mensagem.toLowerCase();
    if (m.includes('token') && (m.includes('expired') || m.includes('invalid')))
        return new ErroDeConta('Código inválido ou vencido. Peça um novo e tente de novo.');
    if (m.includes('email') && (m.includes('invalid') || m.includes('validate')))
        return new ErroDeConta('Este endereço de e-mail não parece válido.');
    if (m.includes('rate limit') || m.includes('security purposes'))
        return new ErroDeConta('Muitos pedidos em pouco tempo. Espere um minuto e tente de novo.');
    if (m.includes('signups not allowed'))
        return new ErroDeConta('Este e-mail não tem conta. Peça acesso a quem administra o app.');
    return new ErroDeConta(mensagem);
}
export async function pedirCodigo(cliente, email) {
    const { error } = await cliente.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
    });
    if (error)
        throw traduzirErro(error.message);
}
export async function confirmarCodigo(cliente, email, codigo) {
    const { error } = await cliente.auth.verifyOtp({
        email,
        token: codigo.trim(),
        type: 'email',
    });
    if (error)
        throw traduzirErro(error.message);
}
export async function sair(cliente) {
    const { error } = await cliente.auth.signOut();
    if (error)
        throw traduzirErro(error.message);
}
export function passkeysDisponiveis(cliente) {
    return (typeof cliente.auth.registerPasskey === 'function' &&
        typeof globalThis.PublicKeyCredential !== 'undefined');
}
export async function registrarPasskey(cliente) {
    if (!passkeysDisponiveis(cliente))
        throw new ErroDeConta('Passkeys não disponíveis neste aparelho.');
    const { error } = await cliente.auth.registerPasskey();
    if (error)
        throw traduzirErro(error.message);
}
/**
 * Quantas passkeys a conta conectada tem. Serve para a tela dizer se o
 * Face ID já pegou neste aparelho — depois de registrar, "Ativar Face ID"
 * sozinho faz o usuário achar que não funcionou.
 *
 * Sem a guarda de `passkeysDisponiveis`: listar é uma chamada HTTP comum,
 * não a cerimônia do WebAuthn, e quem chama já decidiu se mostra a seção.
 * O método público é `auth.passkey.list()` (o `listPasskeys` do
 * GoTrueClient é privado nesta versão e o `auth.admin` exige a chave de
 * serviço).
 */
export async function listarPasskeys(cliente) {
    const { data, error } = await cliente.auth.passkey.list();
    if (error)
        throw traduzirErro(error.message);
    // `data?.length ?? 0` transformaria uma resposta em outro formato (o corpo
    // do `GET /passkeys` envelopado, por exemplo) em "nenhuma chave" — e a tela
    // voltaria a dizer "Ativar Face ID" para quem já tem passkey, sem sinal
    // nenhum. Formato inesperado é falha, e falha aparece.
    if (!Array.isArray(data))
        throw new ErroDeConta('Não foi possível ler as chaves desta conta.');
    return data.length;
}
export async function entrarComPasskey(cliente) {
    if (!passkeysDisponiveis(cliente))
        throw new ErroDeConta('Passkeys não disponíveis neste aparelho.');
    const { error } = await cliente.auth.signInWithPasskey();
    if (error)
        throw traduzirErro(error.message);
}
/**
 * Um canal só, com uma assinatura por tabela: `postgres_changes` exige
 * `table` — sem ele o servidor não devolve mudança nenhuma, e o filtro por
 * `user_id` também é por tabela. O `aoEstado` repassa o ciclo de vida do
 * canal (SUBSCRIBED, CHANNEL_ERROR, TIMED_OUT, CLOSED) para quem assina.
 */
export function realtimeSupabase(cliente, userId) {
    return (aoMudar, aoEstado) => {
        for (const orfao of cliente.getChannels())
            if (orfao.topic === 'realtime:sheafyield-mudancas')
                void cliente.removeChannel(orfao);
        // O socket do Realtime carrega o próprio token, e ele não é o mesmo
        // objeto que a sessão do GoTrue: quem entra depois de o cliente ter sido
        // criado — que é o caso normal deste app, porque o motor parte antes de
        // haver conta — deixa o socket com o token de antes. O `filter` de cada
        // assinatura é `user_id=eq.<id>`, e a RLS do canal o confere contra o
        // JWT do socket: com o token velho, o servidor devolve `CHANNEL_ERROR`
        // ou um canal calado, e o app parece ter Realtime sem receber nada.
        // Sem argumento, o `setAuth` relê o token da sessão corrente.
        let canal = cliente.channel('sheafyield-mudancas');
        for (const tabela of TABELAS) {
            canal = canal.on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: tabela,
                filter: `user_id=eq.${userId}`,
            }, () => {
                aoMudar();
            });
        }
        // O `subscribe` vai na continuação do `setAuth`, e não na linha de
        // baixo, porque **`setAuth` é assíncrono** (realtime-js 2.115,
        // `RealtimeClient.js`: ele é `async` e só escreve `accessTokenValue`
        // depois de esperar) enquanto `RealtimeChannel.subscribe` lê
        // `socket.accessTokenValue` de forma **síncrona**, ao montar o payload
        // do `phx_join`. Chamados no mesmo bloco síncrono, o aperto de mão sai
        // com o token velho — exatamente o sintoma que este conserto existe
        // para matar. Na continuação, ele sai com o token novo.
        //
        // `then(assinar, assinar)`: se a releitura do token falhar, assinar com
        // o que houver é melhor do que não assinar — o pior caso volta a ser o
        // de antes (canal calado), e não "sem Realtime nenhum".
        //
        // A trava `desligado` não é zelo: `subscribe` age justamente quando o
        // canal está `closed`, e `unsubscribe` num canal que nunca assinou o
        // deixa nesse estado — sem ela, a assinatura atrasada faria um `join`
        // completo no canal que acabou de ser fechado, e ainda reconectaria o
        // socket, deixando canal fantasma do usuário anterior.
        let desligado = false;
        const assinar = () => {
            if (desligado)
                return;
            canal.subscribe((estado, erro) => {
                aoEstado?.(estado, erro);
            });
        };
        void cliente.realtime.setAuth().then(assinar, assinar);
        // Desligar fecha o **canal**, não o retorno do `subscribe`: quem desliga
        // pode chegar antes de o `setAuth` resolver, e aí não há retorno nenhum.
        return () => {
            desligado = true;
            void canal.unsubscribe();
        };
    };
}
//# sourceMappingURL=conta.js.map